/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 01/02/2022 09:07
 */

/**
 * List of DataTable parameters
 */
import { BehaviorSubject, of } from 'rxjs';
import { DropdownDirectionEnum } from '../../enum/dropdown-direction-enum.type';
import { RadioType } from '../../enum/radio-type.enum';
import { TableStorageSettings } from '../../interface/table-storage-settings';
import { DropdownAction } from '../../tool/action/dropdown-action';
import { MultiAction } from '../../tool/action/multi-action';
import { RadioAction } from '../../tool/action/radio-action';
import { SimpleAction } from '../../tool/action/simple-action';
import { StandardAction } from '../../tool/action/standard-action';
import { SwitchAction } from '../../tool/action/switch-action';
import { TableAction } from '../../tool/action/table-action';
import { TableSwitchAction } from '../../tool/action/table-switch-action';
import { GroupAction } from '../group-action';
import { DataTableServerExport } from './data-table-server-export';

export class DataTableOptions {
  get isTotalShown(): boolean {
    if (this.showTotalAction) {
      this.showTotalAction.isActive = this._isTotalShown;
    }
    return this._isTotalShown;
  }

  set isTotalShown(value: boolean) {
    this._isTotalShown = value;
  }
  get showPagination(): boolean {
    return this._showPagination;
  }

  set showPagination(value: boolean) {
    this._showPagination = value;
  }

  /**
   * @deprecated use showPagination
   */
  set showFooter(value: boolean) {
    this._showPagination = value;
  }
  // region Setters/Getters

  // region 1st group
  get showFilter(): boolean {
    return this._showFilter;
  }

  set showFilter(value: boolean) {
    this._showFilter = value;
    this.isFilterShown = value;
    this.initDisplayActions();
  }

  get showDelete(): boolean {
    return this._showDelete;
  }

  set showDelete(value: boolean) {
    this._showDelete = value;
    this.initCrudActions();
  }

  get showEdit(): boolean {
    return this._showEdit;
  }

  set showEdit(value: boolean) {
    this._showEdit = value;
    this.initCrudActions();
  }

  get showRefresh(): boolean {
    return this._showRefresh;
  }

  set showRefresh(value: boolean) {
    this._showRefresh = value;
    this.initDisplayActions();
  }

  set showReset(value: boolean) {
    this._showReset = value;
    this.initMoreAction();
  }

  get showReset(): boolean {
    return this._showReset;
  }

  get showTotal(): boolean | 'switch' {
    return this._showTotal;
  }

  set showTotal(value: boolean | 'switch') {
    this._showTotal = value;
    if (value === true) {
      this._isTotalShown = value;
    }
    this.initDisplayActions();
  }

  get showDetails(): boolean {
    return this._showDetails;
  }

  set showDetails(value: boolean) {
    this._showDetails = value;
    this.initDisplayActions();
  }
  // endregion

  // region 2nd group
  get showRowHeight(): boolean {
    return this._showRowHeight;
  }
  set showRowHeight(value: boolean) {
    this._showRowHeight = value;
    this.isRowHeightShown = value;
    this.initMoreAction();
  }

  get showDetailDisplay(): boolean {
    return this._showDetailDisplay;
  }

  set showDetailDisplay(value: boolean) {
    this._showDetailDisplay = value;
    this.initDesignActions();
  }

  get showColumnSelector(): boolean {
    return this._showColumnSelector;
  }

  set showColumnSelector(value: boolean) {
    this._showColumnSelector = value;
    this.isColumnSelectorShown = value;
    this.initDesignActions();
  }

  get rowHeight(): 'S' | 'M' | 'L' {
    return this._rowHeight;
  }

  set rowHeight(value: 'S' | 'M' | 'L') {
    this._rowHeight = value;
    this.rowHeightValue = value;
    if (this.rowHeightAction) {
      const action = this.rowHeightAction.items.find(
        (it) => it.name === `row-height-${this._rowHeight}`,
      );
      if (action) {
        this.rowHeightAction.selectItem(action, true);
      }
    }
  }
  get detailDisplay(): 'none' | 'bottom' | 'right' {
    return this._detailDisplay;
  }

  set detailDisplay(value: 'none' | 'bottom' | 'right') {
    this._detailDisplay = value;
    if (
      this.detailDisplayAction &&
      this.detailDisplayAction.currentValue.name !== this._detailDisplay
    ) {
      this.detailDisplayAction.switch(
        this.detailDisplayAction.values.find(
          (a) => a.name === this._detailDisplay,
        ),
      );
      this.detailDisplayAction.icon =
        this.detailDisplayAction.currentValue.icon;
      this.detailDisplayAction.secondaryIcon =
        this.detailDisplayAction.currentValue.secondaryIcon;
    }
  }
  // endregion

  // region 3th group
  get showCsv(): boolean {
    return this._showCsv;
  }

  set showCsv(value: boolean) {
    this._showCsv = value;
    this.initExportActions();
  }
  get showPdf(): boolean {
    return this._showPdf;
  }

  set showPdf(value: boolean) {
    this._showPdf = value;
    this.initExportActions();
  }

  public showColumnAction: DropdownAction | undefined = undefined;
  public showTotalAction: SwitchAction<any>;

  public displayColumnPosition: 'left' | 'origin' = 'left';
  public canMoveColumn = true;

  private _showReset: boolean;

  constructor(editionMode?: 'form' | 'enum') {
    if (editionMode) {
      this.edition = {
        editable: true,
        editMode: editionMode,
        addLine: true,
        removeLine: true,
        saveLine: true,
      };
    } else {
      this.edition = {
        editable: false,
      };
    }
    this.groupActions = [
      new GroupAction('crud'),
      new GroupAction('display'),
      new GroupAction('design'),
      new GroupAction('dynamic'),
      new GroupAction('export'),
    ];
  }

  /**
   * Count of data for each page
   */
  public paginationStep = 100;

  /**
   * false to disable row selection
   */
  public selection = true;

  /**
   * If selection is true
   * Allows the user to deselect a row by clicking on it again.
   * Otherwise the line will remain selected as long as another is not
   */
  public deselection = true;

  /**
   * Show delete action
   */
  private _showDelete = false;

  /**
   * Mark style of selection
   */
  public selectionStyle: 'marked' | 'none' = 'marked';

  /**
   * Default selected row index
   */
  public defaultSelection?: number;

  /**
   * Display left toolbar (export, column display...)
   * False to hide all left tool
   */
  public showTools = true;

  /**
   * Display pagination footer
   *   @deprecated use showPagination
   */

  private _showFooter = true;

  private _showPagination = true;

  /**
   * Display column selection tool
   */
  private _showColumnSelector = true;

  /**
   * activate pdf generator option
   * true to activate
   */
  private _showPdf?: boolean;

  /**
   * Activate csv generator option
   * true to activate
   */
  private _showCsv?: boolean;

  /**
   * Delegate the csv / pdf exports to the server
   */
  public serverExport?: DataTableServerExport;

  /**
   * Activate details display tool
   * true to activate
   */
  private _showDetails?: boolean;

  /**
   * Show details display selector
   * true to activate
   */
  private _showDetailDisplay?: boolean;

  /**
   * Current detail display
   */
  private _detailDisplay: 'none' | 'bottom' | 'right' = 'bottom';

  public displayDetailOnSelection = false;

  /**
   *  Show total.
   */
  private _showTotal: true | false | 'switch';

  /**
   * is total display on the table
   */
  private _isTotalShown: boolean;

  /**
   * Current row height
   */
  private _rowHeight: 'S' | 'M' | 'L' = 'M';

  /**
   * Activate refresh tool
   * true to activate
   */
  private _showRefresh?: boolean; // Show Refresh option

  /**
   * Activate row-size tool
   * true to activate
   */
  private _showRowHeight?: boolean;

  /**
   * Activate filter tool to filter all columns in the table
   * true to activate
   */
  private _showFilter?: boolean;

  private _showEdit?: boolean;

  /**
   * show # in column selector to enable/disable line number
   */
  public showIndex: boolean;

  /**
   * value to enable/disable the line number
   */
  public index: boolean;

  /**
   * If key is not null or undefined or empty, store the structure when the user drag/drop, resize...column
   */
  public storageKey?: string;

  /**
   * Set true / false to save different settings on settingsService
   */
  public storageSettings: TableStorageSettings;
  /**
   * Set an array of specifics actions managed by the client.
   */
  public actions: SimpleAction<any>[];

  /**
   * Array of actions managed by client.
   */
  public groupActions: GroupAction[] = [];

  public rightActions: GroupAction[] = [];

  /**
   * All options used when table is in edit mode
   */
  public edition: {
    /**
     * True if table is editable
     */
    editable: boolean;
    /**
     * Form to load a classic edit-table
     * Enum to load a field value edit-table
     */
    editMode?: 'form' | 'enum';
    /**
     * False to disable add row
     */
    addLine?: boolean;
    /**
     * False to disable remove row
     */
    removeLine?: boolean;

    /**
     * False to disable save and lock row
     */
    saveLine?: boolean;
  };

  public multipleSelection = false;
  public showHeaderFullSelection = true;

  public rowActions: SimpleAction<any>[];

  public openDetailsOnClick = false;

  // public detailDisplayAction: MultiAction<any>;
  public detailDisplayAction: RadioAction;
  public rowHeightAction: DropdownAction;
  public isFilterShown: boolean;

  public isColumnSelectorShown: boolean;

  private _columnSelectorDropdownDirection: DropdownDirectionEnum = 'down';
  public get columnSelectorDropdownDirection(): DropdownDirectionEnum {
    return this._columnSelectorDropdownDirection;
  }
  public set columnSelectorDropdownDirection(value: DropdownDirectionEnum) {
    this._columnSelectorDropdownDirection = value;
    if (this.showColumnAction) {
      this.showColumnAction.direction = value;
    }
  }

  public isRowHeightShown: boolean;
  public rowHeightValue: 'S' | 'M' | 'L' = 'L';

  public groupColumn: boolean;
  public openGroupsOnColumnGrouping = false;

  public enableViews = false;

  public moreActionObs = new BehaviorSubject<StandardAction[]>([]);
  public moreAction = new DropdownAction(
    'more',
    'ellipsis-v',
    this.moreActionObs,
    null,
    true,
  );

  private initDisplayActions() {
    const group = new GroupAction('display');
    if (this.isFilterShown) {
      const action = new SwitchAction(
        'filterView',
        'filter',
        'dataTable.filters',
      );
      action.tooltip = 'dataTable.tooltip.filters';
      group.actions.push(action);
    }
    if (this._showRefresh) {
      const action = new SimpleAction(
        'refresh',
        'sync',
        null,
        'dataTable.reload',
      );
      action.tooltip = 'dataTable.tooltip.refresh';
      group.actions.push(action);
    }
    if (this._showDetails) {
      const action = new MultiAction(
        'detailsView',
        'search-plus',
        'dataTable.detailView',
      );
      action.tooltip = 'dataTable.tooltip.detailView';
      action.step = [{ icon: 'search-plus' }, { icon: 'search-minus' }];
      group.actions.push(action);
    }
    if (this._showTotal === 'switch') {
      this.showTotalAction = new SwitchAction(
        'totalView',
        'sigma',
        'dataTable.totalView',
      );
      this.showTotalAction.tooltip = 'dataTable.tooltip.totalView';
      group.actions.push(this.showTotalAction);
    }
    this.replaceGroupAction(group);
  }

  private initDesignActions() {
    const group = new GroupAction('design');

    if (this.showDetailDisplay) {
      const right = new MultiAction(
        'right',
        'eye',
        'dataTable.detailDisplay.right',
        'right-to-line',
      );
      const bottom = new MultiAction(
        'bottom',
        'eye',
        'dataTable.detailDisplay.bottom',
        'down-to-line',
      );
      const none = new StandardAction(
        'none',
        'eye-low-vision',
        'dataTable.detailDisplay.none',
      );
      const actions = [right, bottom, none];
      this.detailDisplayAction = new RadioAction(
        'detailDisplay',
        'eye',
        of(actions),
        null,
        RadioType.ICO,
        actions.find((a) => a.name === this._detailDisplay),
      );
      this.detailDisplayAction.tooltip = 'dataTable.tooltip.detailDisplay';
      group.actions.push(this.detailDisplayAction);
    }

    if (this.isColumnSelectorShown) {
      this.showColumnAction = new DropdownAction(
        'showColumn',
        'columns',
        of([]),
        'dataTable.column',
      );
      this.showColumnAction.isMultiple = true;
      this.showColumnAction.withSearch = true;
      this.showColumnAction.tooltip = 'dataTable.tooltip.column';
      this.showColumnAction.direction = this._columnSelectorDropdownDirection;
      group.actions.push(this.showColumnAction);
    }
    this.replaceGroupAction(group);
  }

  private initMoreAction() {
    const array = [];
    if (this.isRowHeightShown) {
      this.rowHeightValue = this._rowHeight;
      this.rowHeightAction = new DropdownAction(
        'rowHeight',
        '',
        of([
          new StandardAction(
            'row-height-L',
            ' icon-letter letter-L',
            'dataTable.height.L',
          ),
          new StandardAction(
            'row-height-M',
            ' icon-letter letter-M',
            'dataTable.height.M',
          ),
          new StandardAction(
            'row-height-S',
            ' icon-letter letter-S',
            'dataTable.height.S',
          ),
        ]),
        'dataTable.height.title',
        true,
      );
      this.rowHeightAction.tooltip = 'dataTable.tooltip.rowHeight';
      this.checkRowHeightValue();
      array.push(this.rowHeightAction);
    }
    if (this._showReset) {
      array.push(
        new StandardAction(
          'reset',
          'table',
          'dataTable.reset',
          'dataTable.tooltip.reset',
          'right',
          'refresh',
        ),
      );
    }
    if (array.length > 0) {
      this.moreActionObs.next(array);
      if (!this.rightActions.find((gr) => gr.id === 'more')) {
        this.rightActions.push(new GroupAction('more', [this.moreAction]));
      }
    } else {
      const index = this.rightActions.findIndex((gr) => gr.id === 'more');
      if (index >= 0) {
        this.rightActions.splice(index, 1);
      }
    }
  }

  private initExportActions() {
    const group = new GroupAction('export');
    if (this.showPdf || this.showCsv) {
      const items = [];
      if (this.showPdf) {
        items.push(new StandardAction('pdf', 'file-pdf', 'PDF'));
      }
      if (this.showCsv) {
        items.push(new StandardAction('csv', 'file-csv', 'CSV'));
      }
      const action = new DropdownAction(
        'export',
        'download',
        of(items),
        'dataTable.download',
      );
      action.tooltip = 'dataTable.tooltip.download';
      group.actions.push(action);
    }
    this.replaceGroupAction(group);
  }

  private initCrudActions() {
    const group = new GroupAction('crud');
    if (this._showEdit) {
      const action = new TableSwitchAction('edit', 'edit', 'dataTable.edit');
      action.tooltip = 'dataTable.tooltip.edit';
      group.actions.push(action);
    }
    if (this._showDelete) {
      const action = new TableAction('delete', 'trash-alt', 'dataTable.delete');
      action.tooltip = 'dataTable.tooltip.delete';
      group.actions.push(action);
    }
    this.replaceGroupAction(group);
  }

  findGroupById(id: string): GroupAction {
    return this.groupActions.find((group) => group.id === id);
  }

  findIndexGroupById(id: string): number {
    return this.groupActions.findIndex((group) => group.id === id);
  }

  replaceGroupAction(group: GroupAction) {
    if (group.actions && group.actions.length > 0) {
      const i = this.groupActions.findIndex((g) => g.id === group.id);
      if (i >= 0) {
        this.groupActions.splice(i, 1, group);
      } else {
        this.groupActions.push(group);
      }
    }
  }

  addGroupAction(
    group: GroupAction,
    index?: number,
    position: 'right' | 'left' = 'right',
  ) {
    const array = position === 'right' ? this.groupActions : this.rightActions;
    if (index !== null && index !== undefined) {
      array.splice(index, 0, group);
    } else {
      array.push(group);
    }
  }

  private checkRowHeightValue() {
    let action: StandardAction;
    switch (this.rowHeightValue) {
      case 'L':
        action = this.rowHeightAction.items[0];
        break;
      case 'M':
        action = this.rowHeightAction.items[1];
        break;
      case 'S':
        action = this.rowHeightAction.items[2];
        break;
    }
    /* istanbul ignore else */
    if (action) {
      this.rowHeightAction.selectItem(action, true);
    }
  }
}
